import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { formatDateTime } from '../utils/dateFormat';
import '../styles/AdminOrders.css';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [notesEdit, setNotesEdit] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, search, page]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/orders/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Erreur stats:', err);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const res = await api.get('/admin/orders', { params });
      setOrders(res.data.orders);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      setError(err.response?.data?.message || t('errors.loadError'));
      if (err.response?.status === 403) navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetail = async (id) => {
    try {
      const res = await api.get(`/admin/orders/${id}`);
      setSelectedOrder(res.data);
      setNotesEdit(res.data.admin_notes || '');
    } catch (err) {
      setError(t('admin.orders.loadDetailError'));
    }
  };

  const handleConfirmDelivery = async (id) => {
    if (!window.confirm(t('confirms.confirmDeliveryAdmin'))) return;
    try {
      setActionLoading(id);
      await api.post(`/admin/orders/${id}/confirm-delivery`);
      fetchOrders();
      fetchStats();
      if (selectedOrder?.id === id) fetchOrderDetail(id);
    } catch (err) {
      setError(err.response?.data?.message || t('errors.unknown'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRefund = async (id) => {
    if (!window.confirm(t('confirms.refundBuyer'))) return;
    try {
      setActionLoading(id);
      await api.post(`/admin/orders/${id}/refund`);
      fetchOrders();
      fetchStats();
      if (selectedOrder?.id === id) fetchOrderDetail(id);
    } catch (err) {
      setError(err.response?.data?.message || t('errors.unknown'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm(t('confirms.cancelOrder'))) return;
    try {
      setActionLoading(id);
      await api.post(`/admin/orders/${id}/cancel`);
      fetchOrders();
      fetchStats();
      if (selectedOrder?.id === id) fetchOrderDetail(id);
    } catch (err) {
      setError(err.response?.data?.message || t('errors.unknown'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveNotes = async (id) => {
    try {
      await api.put(`/admin/orders/${id}/notes`, { notes: notesEdit });
      if (selectedOrder?.id === id) {
        setSelectedOrder({ ...selectedOrder, admin_notes: notesEdit });
      }
    } catch (err) {
      setError(t('admin.orders.saveNotesError'));
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return 'https://via.placeholder.com/50x50?text=?';
    return `${API_URL.replace('/api', '')}/uploads/${imagePath}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateTime(dateStr);
  };

  const statusLabels = {
    'paid': t('status.paid'),
    'shipped': t('status.shipped'),
    'completed': t('status.completed'),
    'cancelled': t('status.cancelled'),
    'refunded': t('status.refunded'),
    'disputed': t('status.dispute')
  };

  const statusClasses = {
    'paid': 'status-paid',
    'shipped': 'status-shipped',
    'completed': 'status-completed',
    'cancelled': 'status-cancelled',
    'refunded': 'status-refunded',
    'disputed': 'status-disputed'
  };

  const canConfirm = (status) => ['paid', 'shipped', 'disputed'].includes(status);
  const canRefund = (status) => ['paid', 'shipped', 'disputed'].includes(status);
  const canCancel = (status) => !['completed', 'refunded', 'cancelled'].includes(status);

  return (
    <div className="admin-orders">
      <div className="admin-orders-header">
        <h1>{t('admin.orders.title')}</h1>
        <button className="btn-back" onClick={() => navigate('/admin')}>{t('common.backToDashboard')}</button>
      </div>

      {error && <div className="admin-orders-error">{error} <button onClick={() => setError('')}>x</button></div>}

      {/* Stats bar */}
      {stats && (
        <div className="orders-stats-bar">
          <div className="orders-stat disputed" onClick={() => { setStatusFilter('disputed'); setPage(1); }}>
            <span className="stat-value">{stats.disputed || 0}</span>
            <span className="stat-label">{t('admin.orders.disputes')}</span>
          </div>
          <div className="orders-stat overdue" onClick={() => { setStatusFilter('shipped'); setPage(1); }}>
            <span className="stat-value">{stats.overdue || 0}</span>
            <span className="stat-label">{t('admin.orders.overdue')}</span>
          </div>
          <div className="orders-stat shipped" onClick={() => { setStatusFilter('shipped'); setPage(1); }}>
            <span className="stat-value">{stats.shipped || 0}</span>
            <span className="stat-label">{t('admin.orders.shipped')}</span>
          </div>
          <div className="orders-stat paid" onClick={() => { setStatusFilter('paid'); setPage(1); }}>
            <span className="stat-value">{stats.paid || 0}</span>
            <span className="stat-label">{t('admin.orders.paid')}</span>
          </div>
          <div className="orders-stat completed" onClick={() => { setStatusFilter('completed'); setPage(1); }}>
            <span className="stat-value">{stats.completed || 0}</span>
            <span className="stat-label">{t('admin.orders.completed')}</span>
          </div>
          <div className="orders-stat total" onClick={() => { setStatusFilter(''); setPage(1); }}>
            <span className="stat-value">{stats.total || 0}</span>
            <span className="stat-label">{t('common.total')}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="orders-filters">
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">{t('admin.orders.allStatuses')}</option>
          <option value="paid">{t('status.paid')}</option>
          <option value="shipped">{t('status.shipped')}</option>
          <option value="completed">{t('status.completed')}</option>
          <option value="cancelled">{t('status.cancelled')}</option>
          <option value="refunded">{t('status.refunded')}</option>
          <option value="disputed">{t('status.dispute')}</option>
        </select>
        <input
          type="text"
          placeholder={t('admin.orders.searchPlaceholder')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {loading ? (
        <div className="admin-orders-loading">{t('common.loading')}</div>
      ) : (
        <>
          {/* Orders table */}
          <div className="orders-table-wrapper">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>{t('common.id')}</th>
                  <th>{t('common.title')}</th>
                  <th>{t('common.buyer')}</th>
                  <th>{t('common.seller')}</th>
                  <th>{t('common.amount')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('common.date')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr><td colSpan="8" className="no-orders">{t('admin.orders.noOrders')}</td></tr>
                ) : (
                  orders.map(order => (
                    <tr
                      key={order.id}
                      className={`${order.is_overdue ? 'row-overdue' : ''} ${order.status === 'disputed' ? 'row-disputed' : ''}`}
                    >
                      <td className="order-id">#{order.id}</td>
                      <td className="order-product">
                        <img src={getImageUrl(order.product_image)} alt="" className="order-thumb" />
                        <span className="order-product-title">{order.product_title}</span>
                      </td>
                      <td>{order.buyer_name}</td>
                      <td>{order.seller_name}</td>
                      <td className="order-amount">{order.total_amount?.toFixed(2)} CHF</td>
                      <td>
                        <span className={`order-status ${statusClasses[order.status]}`}>
                          {statusLabels[order.status]}
                        </span>
                        {order.is_overdue === 1 && <span className="overdue-badge">7j+</span>}
                      </td>
                      <td className="order-date">{formatDate(order.created_at)}</td>
                      <td className="order-actions-cell">
                        <button className="btn-detail" onClick={() => fetchOrderDetail(order.id)}>{t('admin.orders.detail')}</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="orders-pagination">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)}>{t('common.previous')}</button>
              <span>{t('common.pageOf', { current: page, total: totalPages })}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>{t('common.next')}</button>
            </div>
          )}
        </>
      )}

      {/* Order detail modal */}
      {selectedOrder && (
        <div className="order-detail-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="order-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="order-detail-header">
              <h2>{t('admin.orders.orderNumber', { id: selectedOrder.id })}</h2>
              <button className="btn-close-modal" onClick={() => setSelectedOrder(null)}>&times;</button>
            </div>

            <div className="order-detail-content">
              {/* Infos principales */}
              <div className="detail-section">
                <h3>{t('admin.orders.information')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">{t('common.title')}</span>
                    <span className="detail-value">{selectedOrder.product_title}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('common.status')}</span>
                    <span className={`order-status ${statusClasses[selectedOrder.status]}`}>
                      {statusLabels[selectedOrder.status]}
                    </span>
                    {selectedOrder.is_overdue === 1 && <span className="overdue-badge">{t('admin.orders.overdueLabel')}</span>}
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('common.buyer')}</span>
                    <span className="detail-value">{selectedOrder.buyer_name} ({selectedOrder.buyer_email})</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('common.seller')}</span>
                    <span className="detail-value">{selectedOrder.seller_name} ({selectedOrder.seller_email})</span>
                  </div>
                </div>
              </div>

              {/* Montants */}
              <div className="detail-section">
                <h3>{t('admin.orders.amounts')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">{t('admin.orders.itemPrice')}</span>
                    <span className="detail-value">{selectedOrder.product_price?.toFixed(2)} CHF</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('admin.orders.shippingFee')}</span>
                    <span className="detail-value">{selectedOrder.shipping_price?.toFixed(2)} CHF</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('admin.orders.protectionFee')}</span>
                    <span className="detail-value">{selectedOrder.buyer_protection?.toFixed(2)} CHF</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('common.total')}</span>
                    <span className="detail-value detail-total">{selectedOrder.total_amount?.toFixed(2)} CHF</span>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="detail-section">
                <h3>{t('admin.orders.timeline')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">{t('admin.orders.createdAt')}</span>
                    <span className="detail-value">{formatDate(selectedOrder.created_at)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('admin.orders.paidAt')}</span>
                    <span className="detail-value">{formatDate(selectedOrder.paid_at)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('admin.orders.shippedAt')}</span>
                    <span className="detail-value">{formatDate(selectedOrder.shipped_at)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('admin.orders.deliveredAt')}</span>
                    <span className="detail-value">{formatDate(selectedOrder.delivered_at)}</span>
                  </div>
                </div>
              </div>

              {/* Litige */}
              {selectedOrder.status === 'disputed' && (
                <div className="detail-section dispute-section">
                  <h3>{t('admin.orders.disputeLabel')}</h3>
                  <div className="detail-grid">
                    <div className="detail-item full-width">
                      <span className="detail-label">{t('admin.orders.disputeReason')}</span>
                      <span className="detail-value dispute-reason">{selectedOrder.dispute_reason}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">{t('admin.orders.disputeOpenedAt')}</span>
                      <span className="detail-value">{formatDate(selectedOrder.dispute_opened_at)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Resolution litige */}
              {selectedOrder.dispute_resolution && selectedOrder.dispute_resolution !== 'none' && (
                <div className="detail-section">
                  <h3>{t('admin.orders.disputeResolution')}</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">{t('admin.orders.resolution')}</span>
                      <span className="detail-value">
                        {selectedOrder.dispute_resolution === 'confirmed_delivery' ? t('admin.orders.resolutionDelivered') : t('admin.orders.resolutionRefunded')}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">{t('admin.orders.resolvedAt')}</span>
                      <span className="detail-value">{formatDate(selectedOrder.dispute_resolved_at)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes admin */}
              <div className="detail-section">
                <h3>{t('admin.orders.adminNotes')}</h3>
                <textarea
                  className="admin-notes-textarea"
                  value={notesEdit}
                  onChange={(e) => setNotesEdit(e.target.value)}
                  placeholder={t('admin.orders.notesPlaceholder')}
                  rows={3}
                />
                <button className="btn-save-notes" onClick={() => handleSaveNotes(selectedOrder.id)}>
                  {t('admin.orders.saveNotes')}
                </button>
              </div>

              {/* Actions */}
              <div className="detail-section detail-actions">
                <h3>{t('common.actions')}</h3>
                <div className="action-buttons-row">
                  {canConfirm(selectedOrder.status) && (
                    <button
                      className="btn-action btn-confirm"
                      onClick={() => handleConfirmDelivery(selectedOrder.id)}
                      disabled={actionLoading === selectedOrder.id}
                    >
                      {actionLoading === selectedOrder.id ? '...' : t('admin.orders.confirmDelivery')}
                    </button>
                  )}
                  {canRefund(selectedOrder.status) && (
                    <button
                      className="btn-action btn-refund"
                      onClick={() => handleRefund(selectedOrder.id)}
                      disabled={actionLoading === selectedOrder.id}
                    >
                      {actionLoading === selectedOrder.id ? '...' : t('admin.orders.refund')}
                    </button>
                  )}
                  {canCancel(selectedOrder.status) && (
                    <button
                      className="btn-action btn-cancel-order"
                      onClick={() => handleCancel(selectedOrder.id)}
                      disabled={actionLoading === selectedOrder.id}
                    >
                      {actionLoading === selectedOrder.id ? '...' : t('admin.orders.cancelOrder')}
                    </button>
                  )}
                  {!canConfirm(selectedOrder.status) && !canRefund(selectedOrder.status) && !canCancel(selectedOrder.status) && (
                    <span className="no-actions">{t('admin.orders.noActions')}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
